import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import toast from 'react-hot-toast';
import { useCalculateShipDates, useConfirmOrder } from '~/api';
import { Modal } from '~/components/overlays';
import { ClipSpinner } from '~/components/ui';
import { ConfirmOrderModal } from '~/pages/order-view/sections/ConfirmOrderModal';
import { extractGraphQLErrorMessages } from '~/utils';

/**
 * Shared confirm-order modal + mutation logic, used by both the seller and
 * buyer "Confirm Order" call to action, so both trigger the exact same flow.
 */
export function useConfirmOrderModal(orderId: string) {
  const { t } = useTranslation('order');
  const navigate = useNavigate();
  const { mutate: confirmOrder, isPending: isConfirmingOrder } =
    useConfirmOrder();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [shipDatesFetchSeq, setShipDatesFetchSeq] = useState(0);
  const { refetch: fetchShipDates, isFetching: isFetchingShipDates } =
    useCalculateShipDates(orderId);
  // Set from each fetch's own result, not the hook's `data` — react-query keeps the last
  // successful `data` on a failed refetch, which would show stale dates instead of the fallback.
  const [shipDates, setShipDates] =
    useState<Awaited<ReturnType<typeof fetchShipDates>>['data']>(undefined);

  const openConfirmModal = () => {
    setShowConfirmModal(true);
    const onShipDatesSettled = (isError: boolean, data: typeof shipDates) => {
      setShipDates(isError ? undefined : data);
      setShipDatesFetchSeq((seq) => seq + 1);
      if (isError) {
        toast.error(t('order.calculate_ship_dates_error'));
      }
    };
    fetchShipDates()
      .then((result) => onShipDatesSettled(result.isError, result.data))
      .catch(() => onShipDatesSettled(true, undefined));
  };

  const handleConfirmOrder = (
    shippedDate: Date,
    deliveryStartDate: Date,
    deliveryEndDate: Date | null
  ) => {
    confirmOrder(
      {
        orderId: orderId,
        shippedDate: shippedDate.toISOString(),
        deliveryStartDate: deliveryStartDate.toISOString(),
        deliveryEndDate: deliveryEndDate
          ? deliveryEndDate.toISOString()
          : undefined,
      },
      {
        onSuccess: () => {
          setShowConfirmModal(false);
          toast.success(t('order.confirmSuccess'));
          navigate(`/orders/view/${orderId}`);
        },
        onError: (error) => {
          toast.error(
            t('order.confirm_error', {
              message: extractGraphQLErrorMessages(error).join(', '),
            })
          );
        },
      }
    );
  };

  const calculatedShippedDate = shipDates?.calculateShipDates?.shippedDate
    ? new Date(shipDates.calculateShipDates.shippedDate)
    : new Date();
  const calculatedDeliveryStartDate = shipDates?.calculateShipDates
    ?.deliveryStartDate
    ? new Date(shipDates.calculateShipDates.deliveryStartDate)
    : new Date();
  const calculatedDeliveryEndDate = shipDates?.calculateShipDates
    ?.deliveryEndDate
    ? new Date(shipDates.calculateShipDates.deliveryEndDate)
    : null;

  const confirmModal = (
    <Modal isOpen={showConfirmModal} onClose={() => setShowConfirmModal(false)}>
      {isFetchingShipDates ? (
        <div className="bg-neut-bg-alt rounded-lg w-md p-6 flex justify-center">
          <ClipSpinner color="currentColor" />
        </div>
      ) : (
        <ConfirmOrderModal
          key={shipDatesFetchSeq}
          initialShippedDate={calculatedShippedDate}
          initialDeliveryStartDate={calculatedDeliveryStartDate}
          initialDeliveryEndDate={calculatedDeliveryEndDate}
          onConfirm={handleConfirmOrder}
          onCancel={() => setShowConfirmModal(false)}
          isConfirming={isConfirmingOrder}
        />
      )}
    </Modal>
  );

  return {
    confirmModal,
    openConfirmModal,
    isConfirmingOrder,
  };
}
